// mission-settings-profile.ts
//
// Tách khỏi mission-workspace.ts theo seam tự nhiên (giữ mỗi file ≤200 dòng): file này
// chỉ DỰNG nội dung (settings mission + profile tool + digest), không chạm đĩa và
// không biết gì về worktree/registry. Nhờ vậy test 60/61 kiểm được luật quyền mà
// không cần tạo git repo.
//
// D06 — luật cốt tử: KHÔNG sao chép `permissions.allow` từ template. Quyền của worker
// đến từ `--allowedTools` trên argv (P2). `permissions.allow` chỉ xuất hiện khi caller
// bật tường minh cờ `permissions_allow_fallback` VÀ cấp đúng tập rules của adapter.

import { createHash } from "node:crypto";
import { missionWorkerDenyRulesFor, type WorkerDenyBindings } from "./mission-worker-deny-rules.js";

/** Profile tool §3.2 (N37) — ID THẬT trong `tools/` của pack. */
export const MISSION_TOOLS_ENABLED = [
  "why-how-what-confirm",
  "git-flow",
  "pull-request-checker",
  "debate-review",
] as const;

/**
 * Tắt TƯỜNG MINH — kể cả `plan-monitor` (vỏ config, collector đọc cờ
 * `plan-monitor.enabled`): không khai thì `profile_digest` phụ thuộc mặc định của pack
 * và sẽ đổi âm thầm khi pack đổi (N43).
 */
export const MISSION_TOOLS_DISABLED = [
  "prompt-scoring",
  "session-hud",
  "team-knowledge",
  "telemetry",
  "plan-monitor",
] as const;

// AUTOPILOT_REQUIRED_HOOK_FILES lives in mission-required-hook-files.ts
// (review 06g M1, line budget); re-exported so importers keep one path.
export { AUTOPILOT_REQUIRED_HOOK_FILES } from "./mission-required-hook-files.js";

// E10 deny rules + 06g worker deny list live in mission-worker-deny-rules.ts
// (pure data); re-exported so existing importers keep one path.
export {
  MISSION_SECRETS_DENY_RULES,
  MISSION_WORKER_DENY_RULES,
  missionWorkerDenyRulesFor,
} from "./mission-worker-deny-rules.js";

export interface MissionHookEntry {
  type: string;
  command: string;
}

export interface MissionHookMatcher {
  matcher?: string;
  hooks: MissionHookEntry[];
}

/** `settings.hooks` — cùng hình dạng Claude Code đọc (event -> matcher[] -> hooks[]). */
export type MissionHooks = Record<string, MissionHookMatcher[]>;

export interface MissionSettings {
  permissions?: { deny?: string[]; allow?: string[] };
  hooks?: MissionHooks;
  [key: string]: unknown;
}

/**
 * 06g R1a + re-audit N7: the host paths bound into the deny list. All four
 * are required at build time — a missing one throws rather than writing a
 * placeholder rule (fail-closed). Optional in the TYPE only so callers can
 * assemble them piecewise; `buildMissionSettings` checks each.
 */
export type MissionSettingsBindings = Partial<WorkerDenyBindings>;

export interface MissionSettingsInput extends MissionSettingsBindings {
  /** B24/D06: mặc định false ⇒ settings mission KHÔNG có key `permissions.allow`. */
  permissionsAllowFallback?: boolean;
  /** Tập rules `spec.allowedTools` của adapter theo kind — P4 cấp, không tự tra. */
  adapterAllowedTools?: string[];
  /** `permissions.deny` kế thừa (được phép), KHÔNG kèm allow. */
  denyRules?: string[];
  /**
   * Audit 2026-09-14 (F4): khối `hooks` cho mission. TRUYỀN VÀO, không tự đọc —
   * file này phải giữ hợp đồng "không chạm đĩa, không biết gì về worktree"
   * (test 60/61 dựa vào đó để kiểm luật quyền mà không cần dựng git repo).
   * Tầng I/O (mission-workspace.ts) mới là nơi dựng khối này từ pack đã ghim.
   *
   * Trước fix: hàm này KHÔNG BAO GIỜ emit key `hooks`, nên mission chạy với 0
   * hook trong khi preflight vẫn báo đạt — hook giới hạn path và
   * dangerous-command-guard đều vắng mặt.
   */
  hooks?: MissionHooks;
}

export interface ToolStateFile {
  tools: Record<string, { enabled: boolean; version: string; appliedAt: string }>;
}

/**
 * Settings mission. Chỉ giữ `deny`; `allow` chỉ khi bật fallback + có tập rules adapter.
 */
export function buildMissionSettings(input: MissionSettingsInput): MissionSettings {
  const settings: MissionSettings = {};
  settings.permissions = {
    deny: [...(input.denyRules ?? []), ...missionWorkerDenyRulesFor(input)],
  };

  // F4: hooks đến từ caller (tầng I/O). Không có ⇒ không emit key, để
  // `computeSettingsProfileDigest` của mission cũ không đổi vô cớ.
  if (input.hooks !== undefined && Object.keys(input.hooks).length > 0) {
    settings.hooks = input.hooks;
  }

  if (input.permissionsAllowFallback === true) {
    const allow = input.adapterAllowedTools ?? [];
    if (allow.length === 0) {
      throw new Error(
        "permissions_allow_fallback=true nhưng không có `adapterAllowedTools`. " +
          "Fallback phải ghi ĐÚNG tập rules `spec.allowedTools` của adapter theo kind (B24) — " +
          "ghi tập rỗng sẽ khoá sạch quyền của worker.",
      );
    }
    settings.permissions = { ...(settings.permissions ?? {}), allow: [...allow] };
  }
  return settings;
}

export function buildToolState(now: Date): ToolStateFile {
  const appliedAt = now.toISOString();
  const tools: ToolStateFile["tools"] = {};
  for (const name of MISSION_TOOLS_ENABLED) {
    tools[name] = { enabled: true, version: "mission-profile", appliedAt };
  }
  for (const name of MISSION_TOOLS_DISABLED) {
    tools[name] = { enabled: false, version: "mission-profile", appliedAt };
  }
  return { tools };
}

/**
 * Digest phản ánh ĐÚNG settings đã ghi + profile đã áp (D06/N36). Sắp key trước khi
 * hash để cùng nội dung luôn ra cùng digest.
 */
export function computeSettingsProfileDigest(
  settings: MissionSettings,
  toolState: { tools: Record<string, { enabled: boolean }> },
): string {
  const profile = Object.fromEntries(
    Object.entries(toolState.tools)
      .map(([name, entry]) => [name, entry.enabled] as const)
      .sort(([a], [b]) => a.localeCompare(b)),
  );
  const canonical = JSON.stringify({ profile, settings: sortDeep(settings) });
  return createHash("sha256").update(canonical, "utf8").digest("hex");
}

function sortDeep(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      out[key] = sortDeep((value as Record<string, unknown>)[key]);
    }
    return out;
  }
  return value;
}
